/**
 * Online payment gateway types shared across the API + the three SPAs.
 *
 * The backend (`apps/api/src/services/gateways`) holds the real `PaymentGateway`
 * interface + provider implementations (secrets never leave the server). What the
 * frontend needs is just (a) the provider id and (b) the discriminated `GatewayHandoff`
 * that tells the SPA how to actually send the customer to pay — uniformly across
 * Dragonpay (redirect) and iPay88 (auto-submit form POST).
 */

export type GatewayProvider = 'dragonpay' | 'ipay88' | 'stripe' | 'stub';

/** Every provider id. The zod method schema derives its enum from this list. */
export const GATEWAY_PROVIDERS = [
  'dragonpay',
  'ipay88',
  'stripe',
  'stub',
] as const satisfies readonly GatewayProvider[];

/**
 * How the SPA hands the customer off to the gateway to pay. A discriminated union
 * so the member/cashier checkout handles every provider with one switch:
 *  - `redirect`   → `window.location.assign(url)` (Dragonpay, Stripe Checkout).
 *  - `form_post`  → build a hidden auto-submitting `<form action=actionUrl>` whose
 *                   hidden inputs are `fields` (iPay88 `entry.asp`).
 *  - `qr`         → render `image` (data URL / URL) and poll `pollUrl` (POS counter).
 */
export type GatewayHandoff =
  | { kind: 'redirect'; url: string }
  | { kind: 'form_post'; actionUrl: string; fields: Record<string, string> }
  | { kind: 'qr'; image: string; pollUrl: string };

/** Provider-agnostic webhook outcome. Only `success` settles the order. */
export type GatewayStatus = 'success' | 'fail' | 'pending' | 'void' | 'unknown';
